import type { RouteRecordRaw } from 'vue-router'
import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useMenuStore } from '@/stores/menu'
import { usePermissionStore } from '@/stores/permission'
import { useSysBrandingStore } from '@/stores/sysBranding'
import { ensureGlobalCountryTree } from '@/api/globalCountryCache'
import { resolveInternalRedirectPath } from '@/utils/safeRedirect'
/** 首页含 ~1MB world GeoJSON + ECharts，必须懒加载，避免拖慢整个路由入口包 */
const loadHomeView = () => import('@/views/home/index.vue')

const loadDashboardView = () => import('@/views/dashboard/index.vue')

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/login/index.vue'),
    meta: { title: '登录', guest: true },
  },
  {
    path: '/dashboard-screen',
    name: 'dashboard-screen',
    component: loadDashboardView,
    /** 头部入口，未必在后台侧边栏菜单中配置，跳过菜单路径校验 */
    meta: { title: '一张网数据大屏', requiresAuth: true, bypassMenuAccess: true },
  },
  {
    path: '/dashboard-screen/region',
    name: 'dashboard-screen-region',
    component: loadDashboardView,
    meta: { title: '大屏地区', requiresAuth: true, bypassMenuAccess: true },
  },
  {
    path: '/dashboard-screen/org',
    name: 'dashboard-screen-org',
    component: loadDashboardView,
    meta: { title: '大屏组织', requiresAuth: true, bypassMenuAccess: true },
  },
  {
    path: '/',
    name: 'home',
    component: loadHomeView,
    meta: { title: '首页', requiresAuth: true, bypassMenuAccess: true },
  },
  {
    path: '/emergency/case/list',
    name: 'emergency-case-list',
    component: () => import('@/views/emergency/case/list.vue'),
    meta: { title: '案件管理', requiresAuth: true },
  },
  {
    path: '/overseas/caseTodo',
    name: 'emergency-case-todo',
    component: () => import('@/views/emergency/case/todo.vue'),
    meta: { title: '待办任务', requiresAuth: true },
  },
  {
    path: '/overseas/caseDone',
    name: 'emergency-case-done',
    component: () => import('@/views/emergency/case/done.vue'),
    meta: { title: '已办任务', requiresAuth: true },
  },
  {
    path: '/overseas/helpReport',
    name: 'emergency-help-report-list',
    component: () => import('@/views/emergency/help-report/list.vue'),
    meta: { title: '报案记录', requiresAuth: true },
  },
  {
    path: '/emergency/help-report/list',
    redirect: '/overseas/helpReport',
  },
  {
    path: '/emergency/situation-report/list',
    name: 'emergency-situation-report-list',
    component: () => import('@/views/emergency/situation-report/list.vue'),
    meta: { title: '情况反映记录', requiresAuth: true },
  },
  {
    path: '/emergency/evaluation/list',
    name: 'emergency-evaluation-list',
    component: () => import('@/views/emergency/evaluation/list.vue'),
    meta: { title: '评价管理', requiresAuth: true },
  },
  {
    path: '/info',
    name: 'info',
    component: loadHomeView,
    meta: { title: '信息管理', requiresAuth: true, moduleRoot: true },
  },
  {
    path: '/info/enterprise/list',
    name: 'info-enterprise-list',
    component: () => import('@/views/info/enterprise/list.vue'),
    meta: { title: '企业管理', requiresAuth: true },
  },
  {
    path: '/info/agency/list',
    name: 'info-agency-list',
    component: () => import('@/views/info/agency/list.vue'),
    meta: { title: '驻外机构', requiresAuth: true },
  },
  {
    path: '/info/repatriation/list',
    name: 'info-repatriation-list',
    component: () => import('@/views/info/repatriation/list.vue'),
    meta: { title: '归国管理', requiresAuth: true },
  },
  {
    path: '/info/personnel/list',
    name: 'info-personnel-list',
    component: () => import('@/views/info/personnel/list.vue'),
    meta: { title: '人员管理', requiresAuth: true },
  },
  {
    path: '/info/departure/list',
    name: 'info-departure-list',
    component: () => import('@/views/info/departure/list.vue'),
    meta: { title: '出国管理', requiresAuth: true },
  },
  {
    path: '/warning',
    name: 'warning',
    component: loadHomeView,
    meta: { title: '安全预警', requiresAuth: true, moduleRoot: true },
  },
  {
    path: '/warning/risk-alert/list',
    name: 'warning-risk-alert-list',
    component: () => import('@/views/warning/risk-alert/list.vue'),
    meta: { title: '风险预警', requiresAuth: true },
  },
  {
    path: '/warning/release/list',
    name: 'warning-release-list',
    component: () => import('@/views/warning/release/list.vue'),
    meta: { title: '预警发布', requiresAuth: true },
  },
  {
    path: '/warning/release/edit/:id?',
    name: 'warning-release-edit',
    component: () => import('@/views/warning/release/edit.vue'),
    meta: { title: '预警编辑', requiresAuth: true },
  },
  {
    path: '/warning/weekly-report/list',
    name: 'warning-weekly-report-list',
    component: () => import('@/views/warning/weekly-report/list.vue'),
    meta: { title: '安全周报', requiresAuth: true },
  },
  {
    path: '/warning/weekly-report/edit/:id?',
    name: 'warning-weekly-report-edit',
    component: () => import('@/views/warning/weekly-report/edit.vue'),
    meta: { title: '周报编辑', requiresAuth: true },
  },
  {
    path: '/warning/information-release/list',
    name: 'warning-information-release-list',
    component: () => import('@/views/warning/information-release/list.vue'),
    meta: { title: '信息发布', requiresAuth: true },
  },
  {
    path: '/warning/information-release/edit/:id?',
    name: 'warning-information-release-edit',
    component: () => import('@/views/warning/information-release/edit.vue'),
    meta: { title: '内容编辑', requiresAuth: true },
  },
  {
    path: '/geo',
    name: 'geo',
    component: loadHomeView,
    meta: { title: '地理信息管理', requiresAuth: true, moduleRoot: true },
  },
  {
    path: '/geo/country/list',
    name: 'geo-country-list',
    component: () => import('@/views/geo/country/list.vue'),
    meta: { title: '国别管理', requiresAuth: true },
  },
  {
    path: '/geo/region/list',
    name: 'geo-region-list',
    component: () => import('@/views/geo/region/list.vue'),
    meta: { title: '地区管理', requiresAuth: true },
  },
  {
    path: '/service',
    name: 'service',
    component: loadHomeView,
    meta: { title: '智能服务', requiresAuth: true, moduleRoot: true },
  },
  {
    path: '/service/ai-assistant/list',
    name: 'service-ai-assistant-list',
    component: () => import('@/views/service/ai-assistant/list.vue'),
    meta: { title: 'AI助手', requiresAuth: true },
  },
  {
    path: '/service/voice-call/list',
    name: 'service-voice-call-list',
    component: () => import('@/views/service/voice-call/list.vue'),
    meta: { title: '语音通话', requiresAuth: true },
  },
  {
    path: '/service/user-message/list',
    name: 'service-user-message-list',
    component: () => import('@/views/service/user-message/list.vue'),
    meta: { title: '用户留言', requiresAuth: true },
  },
  {
    path: '/other/user-message/list',
    name: 'other-user-message-list',
    component: () => import('@/views/service/user-message/list.vue'),
    meta: { title: '用户留言', requiresAuth: true },
  },
  {
    path: '/other',
    name: 'other',
    component: loadHomeView,
    meta: { title: '其他管理', requiresAuth: true, moduleRoot: true },
  },
  {
    path: '/other/user-agreement/list',
    name: 'other-user-agreement-list',
    component: () => import('@/views/other/user-agreement/list.vue'),
    meta: { title: '用户协议管理', requiresAuth: true },
  },
  {
    path: '/other/banner/list',
    name: 'other-banner-list',
    component: () => import('@/views/other/banner/list.vue'),
    meta: { title: 'Banner配置', requiresAuth: true },
  },
  {
    path: '/other/app-version/list',
    name: 'other-app-version-list',
    component: () => import('@/views/other/app-version/list.vue'),
    meta: { title: '版本管理', requiresAuth: true },
  },
  {
    path: '/other/feedback/list',
    name: 'other-feedback-list',
    component: () => import('@/views/other/feedback/list.vue'),
    meta: { title: '意见反馈管理', requiresAuth: true },
  },
  {
    path: '/other/user-agreement/details/:id',
    name: 'other-user-agreement-details',
    component: () => import('@/views/other/user-agreement/details.vue'),
    meta: { title: '用户协议详情', requiresAuth: true },
    props: true,
  },
  {
    path: '/other/message/list',
    name: 'other-message-list',
    component: () => import('@/views/other/message/list.vue'),
    meta: { title: '站内信息', requiresAuth: true },
  },
  {
    path: '/other/quiz-question/list',
    name: 'other-quiz-question-list',
    component: () => import('@/views/other/quiz-question/list.vue'),
    meta: { title: '问答题目管理', requiresAuth: true },
  },
  {
    path: '/other/social-post/list',
    name: 'other-social-post-list',
    component: () => import('@/views/other/social-post/list.vue'),
    meta: { title: '帖子管理', requiresAuth: true },
  },
  {
    path: '/other/social-comment/list',
    name: 'other-social-comment-list',
    component: () => import('@/views/other/social-comment/list.vue'),
    meta: { title: '评论管理', requiresAuth: true },
  },
  {
    path: '/system',
    name: 'system',
    component: loadHomeView,
    meta: { title: '系统管理', requiresAuth: true, moduleRoot: true },
  },
  {
    path: '/system/permission/list',
    name: 'system-permission-list',
    component: () => import('@/views/system/permission/list.vue'),
    meta: { title: '权限管理', requiresAuth: true },
  },
  {
    path: '/system/user/list',
    name: 'system-user-list',
    component: () => import('@/views/system/user/list.vue'),
    meta: { title: '用户管理', requiresAuth: true },
  },
  {
    path: '/system/user-cancel/list',
    name: 'system-user-cancel-list',
    component: () => import('@/views/system/user-cancel/list.vue'),
    meta: { title: '注销申请', requiresAuth: true },
  },
  {
    path: '/system/department/list',
    name: 'system-department-list',
    component: () => import('@/views/system/department/list.vue'),
    meta: { title: '部门管理', requiresAuth: true },
  },
  {
    path: '/system/security-log/list',
    name: 'system-security-log-list',
    component: () => import('@/views/system/security-log/list.vue'),
    meta: { title: '安全日志', requiresAuth: true },
  },
  {
    path: '/system/config/list',
    name: 'system-config-list',
    component: () => import('@/views/system/config/list.vue'),
    meta: { title: '系统配置', requiresAuth: true },
  },
  /** 须置于路由表末尾：未匹配路径统一进入 404（不设 requiresAuth，未登录也可见） */
  {
    path: '/forbidden',
    name: 'forbidden',
    component: () => import('@/views/error/403.vue'),
    meta: { title: '无权限访问', requiresAuth: true, bypassMenuAccess: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/error/404.vue'),
    meta: { title: '页面不存在' },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

router.beforeEach(async (to) => {
  const authStore = useAuthStore()
  const menuStore = useMenuStore()
  const permissionStore = usePermissionStore()
  const sysBranding = useSysBrandingStore()
  const isLoggedIn = authStore.isLoggedIn
  const requiresAuth = to.meta.requiresAuth as boolean | undefined
  const guestOnly = to.meta.guest as boolean | undefined

  if (!sysBranding.loaded) {
    await sysBranding.loadPublic()
  }

  // 设置页面标题（系统名称与后台配置一致）
  const title = to.meta.title as string | undefined
  if (title) {
    document.title = `${title} - ${sysBranding.systemName}`
  }

  // 需登录的页面：未登录则跳转登录
  if (requiresAuth && !isLoggedIn) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  if (requiresAuth && isLoggedIn) {
    void ensureGlobalCountryTree()
    if (!permissionStore.loaded) {
      await permissionStore.loadCodes()
    }
    if (!menuStore.loaded) {
      await menuStore.loadMenus()
    }

    const bypassMenu = Boolean(to.meta.bypassMenuAccess)
    const isModuleRoot = Boolean(to.meta.moduleRoot)

    // 菜单接口失败：不把空菜单当成「无任何权限」，避免除首页外全部 403
    if (menuStore.error) {
      if (to.path !== '/') return '/'
      return true
    }

    if (isModuleRoot) {
      const leaf = menuStore.getFirstAccessiblePathInModule(to.path)
      if (leaf) return leaf
      return { name: 'forbidden', query: { from: to.fullPath } }
    }

    if (!bypassMenu && !menuStore.hasAccessPath(to.path)) {
      return { name: 'forbidden', query: { from: to.fullPath } }
    }
  }

  // 仅游客页面（如登录页）：已登录则跳转首页或 redirect
  if (guestOnly && isLoggedIn) {
    return resolveInternalRedirectPath(to.query.redirect)
  }
})

export default router
